import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../Dialog/Dialog";
import { CREDIT_PACKAGES } from "../../types/partner";
import { useTranslation } from "react-i18next";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { toastService } from "../../services/ToastService";
import { DiscoveryAPI } from "../../api/DiscoveryAPI";
import { useState } from "react";

interface CreditTopUpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreditTopUpModal({ isOpen, onClose }: CreditTopUpModalProps) {
  const { t } = useTranslation();
  const [loadingPackageId, setLoadingPackageId] = useState<string | null>(null);

  const bestValuePackage = CREDIT_PACKAGES.reduce((best, current) => {
    const currentPricePerCredit = current.price / current.credits;
    const bestPricePerCredit = best.price / best.credits;
    return currentPricePerCredit < bestPricePerCredit ? current : best;
  });

  const handlePackageSelect = async (packageId: string) => {
    if (loadingPackageId) return;
    
    setLoadingPackageId(packageId);
    const selectedPackage = CREDIT_PACKAGES.find(pkg => pkg.id === packageId);
    if (selectedPackage) {
      try {
        const response = await DiscoveryAPI.topUpCredits(selectedPackage.credits);
        window.location.href = response.checkoutUrl;
      } catch (error) {
        console.error('Error during credit top-up:', error);
        toastService.showToast(t("credits.top_up.error", "Error during credit top-up"), "error");
        setLoadingPackageId(null);
      }
    }
  };

  const isPackageLoading = (packageId: string) => loadingPackageId === packageId;
  const isAnyPackageLoading = loadingPackageId !== null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">
            {t("credits.top_up.title", "Top Up Your Credits")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {CREDIT_PACKAGES.map((pkg) => {
            const isBestValue = pkg.id === bestValuePackage.id;
            const savings = Math.round((1 - pkg.price / (pkg.credits * 1)) * 100);
            const isLoading = isPackageLoading(pkg.id);
            const isDisabled = isAnyPackageLoading && !isLoading;
            
            return (
              <div
                key={pkg.id}
                className={`relative flex items-center justify-between p-4 rounded-lg border transition-all ${
                  isLoading 
                    ? 'border-primary bg-primary/5 cursor-default' 
                    : isDisabled
                    ? 'border-input bg-muted/50 cursor-not-allowed opacity-60'
                    : isBestValue 
                    ? 'border-primary bg-primary/5 hover:bg-primary/10 cursor-pointer' 
                    : 'border-input hover:border-primary hover:bg-muted/50 cursor-pointer'
                }`}
                onClick={() => !isDisabled && handlePackageSelect(pkg.id)}
              >
                {isBestValue && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-medium">
                      <Sparkles className="h-3 w-3" />
                      {t("credits.top_up.best_value", "Best Value")}
                    </div>
                  </div>
                )}
                
                {isLoading && (
                  <div className="absolute inset-0 bg-primary/5 rounded-lg flex items-center justify-center">
                    <div className="flex items-center gap-2 text-primary">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="text-sm font-medium">{t("global.processing", "Processing...")} </span>
                    </div>
                  </div>
                )}
                
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium text-lg">
                      {pkg.credits} {t("credits.top_up.credits", "Credits")}
                    </h3>
                    {isBestValue && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {savings}% {t("credits.top_up.off", "off")}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t("credits.top_up.per_credit", "{{price}} EUR per credit", {
                      price: (pkg.price / pkg.credits).toFixed(2),
                    })}
                  </p>
                  {isBestValue && (
                    <div className="flex items-center gap-1 text-xs text-primary">
                      <Check className="h-3 w-3" />
                      {t("credits.top_up.most_popular", "Most popular choice")}
                    </div>
                  )}
                </div>

                <div className="text-right">
                  <div className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-500 to-pink-500">
                    {pkg.price} EUR
                  </div>
                  {!isBestValue && savings > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {t("credits.top_up.savings", "Save {{savings}}%", { savings })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mb-2 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded px-3 py-2">
          {t("credits.top_up.expiry_notice", "Notice: Credits expire 30 days after purchase if unused.")}
        </div>
        
        <div className="text-xs text-muted-foreground text-center px-3 py-2 border-t border-gray-100">
          {t("credits.top_up.currency_notice", "Prices may vary depending on your payment currency. Final amount will be shown at checkout.")}
        </div>
      </DialogContent>
    </Dialog>
  );
} 